import { forwardRef, memo, MutableRefObject, useCallback, useContext, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SplitButton from '../../../components/SplitButton'
import Table from '../../../components/Table'

import './index.scss'
import { getLiveMapViewInfo } from '../data'
import useDynamicRef from '../../../hooks/useDynamicRef'
import { DTO_MobileLocation } from '../../../services/calls/types/positions'
import StopSpeedCol from './table/StopSpeedCol'
import LastPosCol from './table/LastPosCol'
import { FlotiMapRef } from '../../../components/FlotiMap/type'
import { LAYER_TYPE } from '../../../components/FlotiMap'
import MobileDetailCol from './table/MobileDetailCol'
import UserContext from '../../../contexts/UserContext/context'
import Input from '../../../components/Input'
import { PreferenceKey } from '../../../contexts/UserContext/types'

type SidebarLiveMap = {
  mapRef: MutableRefObject<FlotiMapRef>
  onFleetChange: (fleets: number[]) => void
  defaultFleets: number[]
}

const LiveMap_Sidebar = memo(forwardRef(({
  mapRef, onFleetChange, defaultFleets
}: SidebarLiveMap, ref) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [view, setView] = useState<string>('VEHICLES')
  const [tableData, setTableData] = useState<DTO_MobileLocation[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fleetDisplay, setFleetDisplay] = useState<number[] | null>(defaultFleets)

  const availableViews = useMemo(() => ({
    VEHICLES: t('Vehicles view'),
    DRIVERS: t('Drivers view')
  }), [t])

  const columnInfo = useMemo(() => {
    const columns = getLiveMapViewInfo(view)
    return columns
  }, [view])

  const fleetOptions = useMemo(() => {
    const options: Record<number, string> = {}
    user.available_fleets.forEach((fleet) => {
      options[fleet.fleetid] = fleet.name
    })
    return options
  }, [user.available_fleets])

  const mappedHeaderCols = useMemo(() => {
    const lookup = {}
    if (columnInfo?.length) {
      columnInfo.forEach((col, index) => {
        const { id } = col
        lookup[id] = {
          ...col,
          label: t(col.label),
          index,
          formatFunction: (val, row) => {
            if (id === 'STOPPED_SPEED') {
              return <StopSpeedCol val={val} data={row} />
            } else if (id === 'LAST_POSITION') {
              const value = val !== '' ? val : row.posdate
              return <LastPosCol val={value} />
            } else if (id === 'DETAILS') {
              return <MobileDetailCol mobileid={val ?? row.mobile} />
            }
            return val
          }
        }
      })
    }
    return lookup
  }, [columnInfo])

  const rows = useMemo(() => {
    const rowArr = []
    if (tableData?.length) {
      tableData.map((mobile) => {
        const newRow = { ...mobile }
        Object.keys(mappedHeaderCols).map((id) => {
          const { path } = mappedHeaderCols[id]
          const value = mobile[path]
          newRow[id] = value ?? ''
        })
        rowArr.push(newRow)
      })
    }
    return rowArr
  }, [tableData, mappedHeaderCols])

  const setData = (data: DTO_MobileLocation[]) => {
    setTableData(data)
  }

  const forwardRefAction = useDynamicRef({
    setData,
    setIsLoading,
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  const zoomToMobile = useCallback((row: Record<string, any>) => {
    const { lat, lon } = row
    mapRef.current.setMapSettings({ center: [lat, lon], zoom: 15 }, true)
    mapRef.current.makeBlink(row.mobileid, LAYER_TYPE.MARKERS)
  }, [mapRef.current])

  const fleetSelectElem = useMemo(() => {
    if (user.available_fleets.length > 1) {
      return (
        <Input
          type="checkboxmultiselect"
          size='l'
          showSelectAll={true}
          selectAllLabel={t('All fleets')}
          placeholder={!fleetDisplay?.length ? t('All fleets') : ''}
          value={fleetDisplay}
          options={fleetOptions}
          onInput={(val) => {
            onFleetChange(val)
            setFleetDisplay(val ?? [])
          }}
        />
      )
    }

    return null
  }, [fleetOptions, user.available_fleets, t, fleetDisplay])

  return (
    <div className="LiveMap__sidebar">
      <SplitButton
        options={availableViews}
        onClick={(val) => setView(val)}
        value={view}
        size="s"
      />
      <Table
        isLoading={isLoading}
        showSearch={true}
        tableHeader={mappedHeaderCols}
        rows={rows}
        rowHeight={24}
        headerRowHeight={42}
        includePageFooter={true}
        onRowClick={(row) => zoomToMobile(row)}
        headerElems={[fleetSelectElem]}
        defaultSort={{ column: 'CAR_NAME', type: 'ascending' }}
        defaultRowSize={user?.preferences?.[PreferenceKey.FLEETLIST_PAGE_LENGTH]}
      />
    </div>
  )
}))

export default LiveMap_Sidebar
